import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHead } from "@/components/PageHead";
import { SeeInPerson } from "@/components/SeeInPerson";
import { PurchaseSteps } from "@/components/PurchaseSteps";
import { img, findProduct } from "@/lib/site";

const downloads = [
  { label: "Technical Statement", type: "PDF" },
  { label: "Installation Guide", type: "PDF" },
  { label: "Care & Maintenance Guide", type: "PDF" },
  { label: "High-Resolution Image", type: "JPG" },
  { label: "Warranty Statement", type: "PDF" },
];

const skuOf = (p) => `HOD-${p.slug.slice(0, 10).toUpperCase()}`;

const PRODUCT_COPY = {
  timber: {
    subline: "natural timber, engineered for interior architecture",
    lead: "A considered timber for interiors that reward the eye up close. Each board is selected for grain, tone and consistency, then finished to a standard that suits both residential and commissioned architectural work.",
    body: "This timber pairs naturally with stone, brass and soft plaster tones. Order a sample to see how it settles in your own light, or speak with our team about volume for a project.",
    note: "This product is supplied unfinished and may require sealing prior to use. Finish requirements vary by application — please confirm at time of enquiry.",
    specs: (p, c) => [
      { label: "SKU", value: skuOf(p) },
      { label: "Species", value: "European Oak" },
      { label: "Construction", value: "Multi-ply engineered core" },
      { label: "Width", value: "150 mm" },
      { label: "Length", value: "2400 mm" },
      { label: "Thickness", value: "12 mm" },
      { label: "Finish", value: "Unfinished / Raw" },
      { label: "Edge Profile", value: "Square edge" },
      { label: "Grade", value: "Prime" },
      { label: "Certification", value: "FSC® Certified" },
      { label: "Application", value: "Wall & Floor" },
      { label: "Collection", value: c.name },
    ],
  },
  "natural-stone": {
    subline: "natural stone, selected for interior and architectural use",
    lead: "Natural stone with honest depth of colour and texture. Quarried and finished for interiors and considered outdoor spaces, each piece carries the individuality of the material.",
    body: "This stone sits beautifully alongside timber, plaster and warm metals. Natural variation in veining and tone is expected and celebrated.",
    note: "Natural stone varies in colour, veining and texture. Sealing is recommended for most applications — please confirm suitability for wet or high-traffic areas.",
    specs: (p, c) => [
      { label: "SKU", value: skuOf(p) },
      { label: "Material", value: "Natural Stone" },
      { label: "Finish", value: "Varies" },
      { label: "Format", value: "Tile / Slab" },
      { label: "Rectified", value: "No" },
      { label: "Thickness", value: "10 mm" },
      { label: "Edge", value: "Tumbled" },
      { label: "Origin", value: "Imported" },
      { label: "Application", value: "Interior / Exterior" },
      { label: "Slip Rating", value: "R10 (varies)" },
      { label: "Sealing", value: "Recommended" },
      { label: "Collection", value: c.name },
    ],
  },
  "custom-carpet": {
    subline: "custom soft flooring, made to order",
    lead: "Bespoke soft flooring, made to order. Woven and tufted to your palette and pattern, our carpet is designed around the space it will live in.",
    body: "From broadloom to custom rugs, specifications are tailored per project. Speak with our team about colour, pattern, pile and dimensions for your space.",
    note: "This is a made-to-order product. Colour, pattern and dimensions are confirmed per project — lead times apply once specification is finalised.",
    specs: (p, c) => [
      { label: "SKU", value: skuOf(p) },
      { label: "Construction", value: "Woven / Tufted" },
      { label: "Fibre", value: "Pure New Zealand Wool" },
      { label: "Width", value: "4 m broadloom" },
      { label: "Pile Height", value: "8–12 mm" },
      { label: "Backing", value: "Action / Felt" },
      { label: "Customisation", value: "Made to order" },
      { label: "Pattern", value: "Bespoke" },
      { label: "Application", value: "Residential / Commercial" },
      { label: "Collection", value: c.name },
    ],
  },
  "carpet-tiles": {
    subline: "modular carpet tiles, made to order",
    lead: "Modular carpet tiles for commercial and residential interiors. Woven and tufted to your palette and pattern, designed around the space they will live in.",
    body: "From loop and textured tiles to broadloom and custom rugs, specifications are tailored per project. Speak with our team about colour, pattern, pile and dimensions for your space.",
    note: "This is a made-to-order product. Colour, pattern and dimensions are confirmed per project — lead times apply once specification is finalised.",
    specs: (p, c) => [
      { label: "Description", value: "Nylon Carpet Tile" },
      { label: "Construction", value: "Tufted Textured Loop" },
      { label: "Face Fibre", value: "100% Nylon BCF, Solution-dyed" },
      { label: "Pile Height", value: "4 ± 0.3 mm" },
      { label: "Total Height", value: "5.9 ± 0.3 mm" },
      { label: "Pile Weight", value: "450 g/m² ± 5%" },
      { label: "Gauge", value: "1/12\"" },
      { label: "Primary Backing", value: "Non-woven Polyester Spunbonded" },
      { label: "Backing", value: "TPE Backing" },
      { label: "Size", value: "50 × 50 cm / 25 × 100 cm" },
      { label: "Flammability", value: "ASTM E648 Class I" },
      { label: "Wear Classification", value: "Heavy Commercial" },
      { label: "Wear Warranty", value: "8 Years" },
      { label: "Collection", value: c.name },
    ],
  },
  "hod-wall-panels": {
    subline: "resilient ceramic wall panels",
    lead: "Large-format resilient ceramic wall panels that reproduce the depth and texture of natural stone — engineered for durability and fast installation.",
    body: "Available across the Inspira and Essentials lines in marble, travertine, terrazzo, concrete and abstract designs. Panels are supplied in large formats for seamless, refined feature walls.",
    note: "Sizes and finishes vary by design. Confirm panel dimensions and availability with House of David before ordering.",
    specs: (p, c) => [
      { label: "SKU", value: skuOf(p) },
      { label: "Material", value: "Resilient Ceramic" },
      { label: "Line", value: p.sub },
      { label: "Panel Size", value: "2600 × 1200 mm" },
      { label: "Thickness", value: "6 mm" },
      { label: "Finish", value: "Matte" },
      { label: "Application", value: "Interior feature walls" },
      { label: "Fire Rating", value: "Class A" },
      { label: "Collection", value: c.name },
    ],
  },
  additional: {
    subline: "specialist applied interior finishes",
    lead: "Specialist finishes that complete an interior — seamless, tactile and applied by skilled hands.",
    body: "Micro cement and artisan plaster deliver a continuous, jointless surface across walls, floors and joinery. Applied by trained applicators to your chosen tone and texture.",
    note: "Applied finishes are installed by trained applicators. Substrate preparation and site conditions affect the final result — please confirm scope at time of enquiry.",
    specs: (p, c) => [
      { label: "SKU", value: skuOf(p) },
      { label: "Type", value: "Applied Finish" },
      { label: "Application", value: "Trowel-applied" },
      { label: "Thickness", value: "2–3 mm" },
      { label: "Finish", value: "Matte / Sealed" },
      { label: "Substrate", value: "Prepared surface" },
      { label: "Sealer", value: "Included" },
      { label: "Use", value: "Wall / Floor / Joinery" },
      { label: "Collection", value: c.name },
    ],
  },
};

export default function ProductDetail() {
  const { slug, product: productSlug } = useParams();
  const found = findProduct(slug, productSlug);
  if (!found) return <Navigate to={`/collections/${slug}`} replace />;
  const { collection, product } = found;

  if (collection.slug === "marble-slabs") {
    return (
      <div data-testid="product-detail" className="pt-28 md:pt-36">
        <section className="edge" data-testid="product-header">
          <div className="mb-6">
            <Link to={`/collections/${slug}`} className="eyebrow hover:text-foreground">
              ← {collection.name}
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <div className="group overflow-hidden bg-secondary/40">
                <img
                  src={img(product.image, 1600)}
                  alt={product.name}
                  className="aspect-square w-full cursor-zoom-in object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
                />
              </div>
            </div>
            <div className="flex flex-col justify-center md:col-span-5">
              <span className="eyebrow">Natural Marble Collection</span>
              <h1 className="mt-5 text-4xl font-light leading-[1.02] tracking-editorial md:text-5xl">
                {product.name}
              </h1>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground" data-testid="marble-coming-soon">
                Information coming soon.
              </p>
              <Link
                to="/contact"
                data-testid="product-consult-cta"
                className="mt-8 inline-block w-fit bg-primary px-8 py-4 text-sm uppercase tracking-[0.16em] text-primary-foreground transition-opacity hover:opacity-90"
              >
                Request a Project Consultation →
              </Link>
            </div>
          </div>
        </section>
        <div className="py-16" />
      </div>
    );
  }

  const copy = PRODUCT_COPY[collection.slug === "custom-carpet-range" ? "custom-carpet" : collection.slug] || PRODUCT_COPY.timber;
  const specs = copy.specs(product, collection);
  const docUrl = collection.brochure?.url || "/assets/brochures/timber-panels.pdf";
  const CARE_PDF = "/assets/brochures/care-maintenance-installation.pdf";
  const downloadsList =
    collection.slug === "natural-stone"
      ? [{ label: "Care, Maintenance & Installation", type: "PDF", href: CARE_PDF }]
      : downloads.map((d) => ({ ...d, href: docUrl }));

  const related = collection.products.filter((p) => p.slug !== product.slug).slice(0, 4);
  const isCarpet = ["custom-carpet", "custom-carpet-range", "carpet-tiles"].includes(collection.slug);

  const copySpec = () => {
    const text =
      `${product.name} — ${collection.name}\n` +
      specs.map((s) => `${s.label}: ${s.value}`).join("\n");
    navigator.clipboard?.writeText(text);
    toast.success("Architectural specification copied to clipboard.");
  };

  return (
    <div data-testid="product-detail" className="pt-28 md:pt-36">
      {/* Header */}
      <section className="edge" data-testid="product-header">
        <div className="mb-6">
          <Link to={`/collections/${slug}`} className="eyebrow hover:text-foreground">
            ← {collection.name}
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            {isCarpet && product.installed ? (
              <>
                <div className="group overflow-hidden bg-secondary/40" data-testid="product-mockup-image">
                  <img src={img(product.installed, 1600)} alt={`${product.name} in situ`} className="aspect-[4/3] w-full cursor-zoom-in object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" />
                </div>
                <div className="group mt-4 max-w-xs overflow-hidden bg-secondary/40" data-testid="product-tile-image">
                  <img src={img(product.image, 1000)} alt={`${product.name} tile`} className="aspect-square w-full cursor-zoom-in object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" />
                </div>
              </>
            ) : (
              <>
                <div className="group overflow-hidden bg-secondary/40">
                  <img src={img(product.image, 1600)} alt={product.name} className={`${collection.slug === "natural-stone" ? "aspect-square" : "aspect-[4/5]"} w-full cursor-zoom-in object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110`} />
                </div>
                {product.installed && (
                  <div className="group mt-4 overflow-hidden bg-secondary/40" data-testid="product-installed-image">
                    <img src={img(product.installed, 1600)} alt={`${product.name} installed`} className="aspect-[4/3] w-full cursor-zoom-in object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110" />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex flex-col justify-center md:col-span-5">
            <span className="eyebrow">{collection.subcategories.join(" • ")}</span>
            <h1 className="mt-5 text-4xl font-light leading-[1.02] tracking-editorial md:text-5xl">
              {product.name}
            </h1>
            <p className="mt-5 text-base text-muted-foreground">
              {product.sub} — {copy.subline}.
            </p>
            <Link
              to="/contact"
              data-testid="product-consult-cta"
              className="mt-8 inline-block w-fit bg-primary px-8 py-4 text-sm uppercase tracking-[0.16em] text-primary-foreground transition-opacity hover:opacity-90"
            >
              Request a Project Consultation →
            </Link>
          </div>
        </div>
      </section>

      {/* Description + note */}
      <section className="edge py-20 md:py-28" data-testid="product-description">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-3">
            <span className="eyebrow">Description</span>
          </div>
          <div className="md:col-span-8 md:col-start-4">
            <p className="text-xl font-light leading-relaxed tracking-editorial md:text-2xl">
              {copy.lead}
            </p>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              {copy.body}
            </p>
            <div className="mt-8 border-l-2 border-foreground bg-secondary/40 p-6" data-testid="product-note">
              <span className="eyebrow">Please Note</span>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {copy.note}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="edge py-16" data-testid="product-specs">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-3">
            <span className="eyebrow">Specifications</span>
            <button
              onClick={copySpec}
              data-testid="copy-spec"
              className="mt-6 block border-b border-foreground pb-1 text-sm uppercase tracking-[0.16em]"
            >
              Copy Architectural Specification
            </button>
          </div>
          <div className="md:col-span-8 md:col-start-4">
            <dl className="grid grid-cols-1 sm:grid-cols-2">
              {specs.map((s) => (
                <div key={s.label} className="flex items-baseline justify-between gap-4 border-b hairline py-4 sm:odd:pr-8 sm:even:pl-8">
                  <dt className="eyebrow">{s.label}</dt>
                  <dd className="text-sm tracking-editorial">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Downloads */}
      {!isCarpet && (
      <section className="edge py-16" data-testid="product-downloads">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-3">
            <span className="eyebrow">Downloads</span>
          </div>
          <div className="md:col-span-8 md:col-start-4">
            <ul className="divide-y hairline">
              {downloadsList.map((d) => (
                <li key={d.label}>
                  <a
                    href={d.href}
                    download
                    data-testid={`download-${d.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="group flex items-center justify-between py-4"
                  >
                    <span className="text-base tracking-editorial">{d.label}</span>
                    <span className="flex items-center gap-4">
                      <span className="eyebrow">{d.type}</span>
                      <span className="transition-transform duration-500 group-hover:translate-y-0.5">↓</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      )}

      {/* Showroom / reference */}
      <SeeInPerson referenceUrl={docUrl} />

      {/* How to purchase */}
      <PurchaseSteps />

      {/* Related products */}
      <section className="edge py-20 md:py-28" data-testid="related-products">
        <div className="mb-10 flex items-baseline justify-between">
          <h2 className="text-2xl font-light tracking-editorial md:text-3xl">Related products</h2>
          <Link to={`/collections/${slug}`} className="eyebrow hover:text-foreground">
            View all →
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-x-5 gap-y-8 md:grid-cols-6">
          {related.map((p) => (
            <Link
              key={p.slug}
              to={`/collections/${slug}/${p.slug}`}
              data-testid={`related-${p.slug}`}
              className="group block"
            >
              <div className="aspect-square overflow-hidden bg-secondary/40">
                <img
                  src={img(p.image, 500)}
                  alt={p.name}
                  className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                />
              </div>
              <h3 className="mt-2 text-xs tracking-editorial">{p.name}</h3>
              <span className="mt-0.5 block eyebrow">{p.sub}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
